import { useEffect, useState } from "react";
import { Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";

// Jun 2026 (Randy) — the AI Assistant's "Length" tab. Mirrors the Public Q&A's
// LengthBump (answer-style presets) but points at the ASSISTANT's outputs: the
// chosen preset is held in SiloAssistant state and shipped on every /buddy/chat
// call, so the length holds even when the LLM model changes.
export default function AssistantLengthPanel({ silo, activePreset, activeCustom, onChange }) {
  const [styles, setStyles] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/s/${silo.slug}/answer-styles`);
        if (cancelled) return;
        setStyles(r.data);
        // Seed the current selection from the silo's saved default the first
        // time (so it matches what the Public Q&A would do by default).
        if (!activePreset) {
          onChange?.({ preset: r.data.active_preset || r.data.silo_default_preset || "free_choice", custom: r.data.active_custom || null });
        }
      } catch {
        if (!cancelled) setStyles({ items: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [silo?.slug]);

  if (loading) {
    return <div className="p-4 text-xs text-[#9b9a96]" data-testid="assistant-length-loading">Loading length options…</div>;
  }
  const items = styles?.items || [];

  return (
    <div className="p-4 space-y-4" data-testid="assistant-length-panel">
      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1">How long should the assistant answer?</p>
        <p className="text-sm leading-relaxed text-[#1C1B1A]">
          Pick a length. The assistant keeps all its thinking power either way — this just shapes how long the written answer comes out, on any model.
        </p>
      </div>
      <div className="space-y-2" data-testid="assistant-length-options">
        {items.map((it) => {
          const isActive = activePreset === it.id && (
            it.id !== "custom" || (activeCustom?.name && activeCustom.name === it.name)
          );
          const isDefault = it.id === styles?.silo_default_preset;
          return (
            <button
              key={`${it.id}-${it.name}`}
              type="button"
              onClick={() => onChange?.({ preset: it.id, custom: it.is_custom ? { name: it.name, min_words: it.min_words, max_words: it.max_words } : null })}
              data-testid={`assistant-length-option-${it.id}`}
              className={`w-full text-left p-3 rounded-lg border transition-all flex items-start gap-3 ${
                isActive ? "border-[#4A5D4E] bg-[#4A5D4E]/10" : "border-[#E5E5E0] bg-white hover:border-[#4A5D4E]"
              }`}
            >
              <div className="mt-0.5 shrink-0">
                {isActive ? (
                  <Check size={16} weight="bold" className="text-[#4A5D4E]" />
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-[#E5E5E0]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-medium text-[#1C1B1A]">{it.name}</span>
                  {isDefault && (
                    <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded text-[#6B6A68] border border-[#E5E5E0]">
                      Silo default
                    </span>
                  )}
                  {it.is_custom && !isDefault && (
                    <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded text-[#4A5D4E] border border-[#4A5D4E]/40">
                      Custom
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-[#6B6A68] mt-0.5 leading-snug">{it.description}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
